import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography
} from "@mui/material";
import { saveAs } from 'file-saver';
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import History from "../../../@history";
import { transFormVehicleData } from "../../../custom/commonExport";
import { getArticlesDataByFile } from "../../../services/article-service/articleService";

const allowedExtensions = ['.csv', '.xlsx', '.xls'];

export default function FileUpload({ openToggleModelForUpload, handleCloseModalForUploadFile, files, setFiles, userData, selectedRegion, setLoading }) {

    // State to store selected files
    const [reading, setReading] = useState(false)
    const [parsedData, setParsedData] = useState([])
    const { i18n } = useTranslation();
    const navigate = useNavigate()

    // Function to handle file selection
    const handleFiles = (selectedFiles) => {
        const file = selectedFiles[0]
        if (!file) {
            toast("Unable to upload the file format!", {
                autoClose: true,
                type: "error"
            })
            return;
        }
        setReading(true)
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!allowedExtensions.includes(ext)) {
            toast("Invalid file format. Please upload a .csv, .xls, or .xlsx file.", { type: "error" });
            return;
        }
        setFiles([file]);

        const reader = new FileReader()
        reader.onload = (event) => {
            try {
                let jsonData;
                if (ext === ".csv") {
                    const text = event.target.result;
                    const workbook = XLSX.read(text, { type: "string" });
                    const sheet = workbook.Sheets[workbook.SheetNames[0]];
                    jsonData = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
                } else {
                    const data = new Uint8Array(event.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const workSheet = workbook.Sheets[sheetName];
                    jsonData = XLSX.utils.sheet_to_json(workSheet, { defval: "", raw: false });
                }

                const sanitizedData = jsonData.map((row) => {
                    const cleanRow = {};
                    for (const key in row) {
                        const value = row[key];
                        const lowerCaseKey = key.toLowerCase()
                        cleanRow[lowerCaseKey] = String(value).normalize("NFC");
                    }
                    return cleanRow;
                });

                setParsedData(sanitizedData);
            } catch (error) {
                console.error("File parsing error:", error);
                toast.error("Failed to parse the file. Please check its format.");
            }
            finally {
                setReading(false);
            }
        }

        if (ext === '.csv') {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    }

    const handleFileChange = (event) => {
        handleFiles(Array.from(event.target.files))
    }

    const handleRemoveFile = () => {
        setFiles([]);
        setReading(false)
    }

    const handleDrop = (event) => {
        event.preventDefault()
        handleFiles(Array.from(event.dataTransfer.files));
    }

    const handleDragOver = (event) => {
        event.preventDefault()
    }

    const handleArticleUploadEvent = async (e) => {
        e.preventDefault()
        if (!parsedData) {
            toast("Unable to upload the file!", {
                type: "error",
                autoClose: true
            })
            return;
        }
        setLoading(true)
        try {
            setFiles([])
            handleCloseModalForUploadFile()
            const articles = parsedData
            if (!articles) {
                toast('Invalid File Or Format', {
                    type: "error",
                    autoClose: true
                })
                return;
            }

            if (articles.length === 0) {
                toast("Please upload at least 1 row data.", {
                    type: "error",
                    autoClose: true,
                })
                return;
            }

            if (articles.length > 20) {
                toast("You can upload a maximum of 20 rows.", {
                    type: "error",
                    autoClose: true
                })
                return;
            }
            const CurrentLanguage = i18n.language
            const region = selectedRegion?.value
            const response = await getArticlesDataByFile({ articles, CurrentLanguage, userData, region })
            let message = response?.message
            if (response && response.status === 200) {
                await exportExcel(response?.data)
                toast(message || "CSV Exported Successfully.", {
                    autoClose: true,
                    type: "success"
                })
            }
            else if (response && response.status === 401) {
                localStorage.removeItem('userData')
                navigate('/login')
                History.push("/login")
                toast('Session Expired.', {
                    autoClose: true,
                    type: 'error'
                })
                return;
            }
            else {
                toast(message || 'Try again!', {
                    autoClose: true,
                    type: "error"
                })
            }
        }
        catch (error) {
            const errorStatus = error.status || null;
            if (errorStatus === 401 || error?.statusCode === 401) {
                localStorage.removeItem('userData')
                navigate('/login')
                History.push("/login")
                toast('Session Expired.', {
                    autoClose: true,
                    type: 'error'
                })
                return;
            }
            else {
                toast('Try Again!', {
                    autoClose: true,
                    type: "error"
                })
            }
        }
        finally {
            setLoading(false)
        }
    }


    const exportExcel = async (data) => {
        try {
            if (!data || !Array.isArray(data) || data.length === 0) {
                console.warn("No data available for Excel export:", data);

                const testSheet = XLSX.utils.json_to_sheet([{ Message: "No data to export" }]);
                const testWorkbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(testWorkbook, testSheet, "Empty");

                const testBlob = XLSX.write(testWorkbook, { bookType: 'xlsx', type: 'array' });
                saveAs(new Blob([testBlob], { type: "application/octet-stream" }), 'empty_export.xlsx');
                return;
            }

            const workbook = XLSX.utils.book_new();

            data.forEach((item, index) => {
                try {
                    // === Data Setup ===
                    const removedSpaceFromArticleNO = item?.ArticleAttributes?.ArticleNo.replace(/\s/g, '')
                    const articleNo = `Article${index + 1}_${removedSpaceFromArticleNO}`;
                    const baseSheetName = articleNo.substring(0, 31); // Excel limit
                    const attrSheetName = `${baseSheetName}_Attributes`.substring(0, 31);

                    // === Transform Vehicle Data ===
                    const vehicleData = item?.VehicleDetails
                        ? transFormVehicleData(item.VehicleDetails)
                        : [];

                    const vehicleSheet = XLSX.utils.json_to_sheet(vehicleData || [], { defval: "" });

                    // === Transform Attributes ===
                    const attributes = item.ArticleAttributes || {};
                    const attributeRows = Object.entries(attributes).map(([key, value]) => ({
                        Attribute: key,
                        Value: value,
                    }));

                    const attributeSheet = XLSX.utils.json_to_sheet(attributeRows, { defval: "" });

                    // === Add to workbook ===
                    XLSX.utils.book_append_sheet(workbook, vehicleSheet, baseSheetName);
                    XLSX.utils.book_append_sheet(workbook, attributeSheet, attrSheetName);

                } catch (innerErr) {
                    console.error("Error processing item:", item, innerErr);
                    toast.error("One of the items failed to export. Check console.");
                }
            });

            const fileName = `All_Articles_${new Date().toISOString().split("T")[0]}.xlsx`;

            const blob = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
                cellStyles: true,
            });

            saveAs(new Blob([blob], { type: "application/octet-stream" }), fileName);

        } catch (error) {
            console.error("Error during full Excel export:", error);
            toast.error("Failed to generate Excel file.");
        }
    };



    return (
        <Dialog
            open={openToggleModelForUpload}
            onClose={handleCloseModalForUploadFile}
            sx={{
                '& .MuiDialog-container': { alignItems: 'center' },
                '& .MuiPaper-root': { width: '100%', borderRadius: 3, padding: 3, maxWidth: "550px" }
            }}
        >
            <DialogTitle sx={{ textAlign: 'left', fontWeight: 'bold', position: "relative" }}>
                Upload File
                <IconButton onClick={handleCloseModalForUploadFile} size="small" sx={{ position: "absolute", right: "5px", top: "-5px" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Box
                    className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg shadow-md w-full max-w-md h-52 border-2 border-dashed border-blue-400 hover:bg-blue-100 transition-all"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}>
                    {/* Label that acts as a clickable area for file selection */}
                    <label htmlFor="file-upload" className="flex flex-col items-center cursor-pointer">
                        <CloudUploadIcon className="text-blue-500" fontSize="large" />
                        <Typography variant="body2" color="primary" className="mt-2">
                            Drag & Drop files here or click to upload
                        </Typography>
                    </label>

                    {/* Hidden file input element for selecting files */}
                    <input id="file-upload" type="file" className="hidden" onChange={handleFileChange} />
                </Box>

                {/* Display the selected file names with progress bars */}
                <Box className="mt-4 w-full">
                    {files.length > 0 && (
                        <List>
                            {files.map((file, index) => (
                                <ListItem key={index} className="p-2 bg-white rounded-lg shadow text-gray-700 text-sm flex items-center">
                                    <ListItemIcon>
                                        {!reading ? (
                                            <CheckCircleIcon className="text-green-500" />
                                        ) : (
                                            <InsertDriveFileIcon className="text-gray-500" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={file.name}
                                        secondary={(file.size / 1024).toFixed(2) + " KB"}
                                    />
                                    <IconButton onClick={() => handleRemoveFile(index)} className="ml-2 text-red-500">
                                        <CancelIcon />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
            <DialogActions className="flex justify-center">
                {/* Upload button styled with MUI and Tailwind */}
                <Button
                    variant="contained"
                    component="label"
                    disabled={!files.length > 0}
                    className={`px-6 py-2 rounded-lg ${files.length === 0 || reading ? '!bg-gray-300 !text-white' : '!bg-blue-500 !text-white hover:!bg-blue-600'}`}
                    onClick={handleArticleUploadEvent}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    );
}
